// RoadmapView - Per-project launch progress by phase
import React, { useState, useMemo, useEffect, useRef } from 'react';
import {
  ArrowLeft, ChevronDown, ChevronRight, Star,
  Pencil, Plus, Trash2, ChevronUp, Check, X,
} from 'lucide-react';
import { ABLE_COLORS } from '../constants';
import { useRoadmapConfig } from '../hooks/useRoadmapConfig';
import { useToast } from './Toast';

const STATUS_COLORS = {
  done: '#4A8B6F',
  ready: '#5A8EB8',
  blocked: '#B8860B',
};

const STATUS_BG = {
  done: 'rgba(74, 139, 111, 0.06)',
  ready: 'rgba(90, 142, 184, 0.06)',
  blocked: 'rgba(184, 134, 11, 0.08)',
};

// blockedBy entries may be task IDs (preferred) or exact title strings (legacy).
function resolveBlocker(ref, byId, byTitle) {
  if (ref == null) return null;
  return byId.get(ref) || byTitle.get(String(ref).toLowerCase()) || null;
}

function computeStatus(task, byId, byTitle) {
  if (task.done) return 'done';
  if (!task.blockedBy || task.blockedBy.length === 0) return 'ready';
  const allBlockersDone = task.blockedBy.every(ref => {
    const blocker = resolveBlocker(ref, byId, byTitle);
    return blocker && blocker.done;
  });
  return allBlockersDone ? 'ready' : 'blocked';
}

function sortPhaseTasks(phaseTasks, statusById, byId, byTitle) {
  const statusOrder = { done: 0, ready: 1, blocked: 2 };
  return [...phaseTasks].sort((a, b) => {
    const sa = statusById.get(a.id) || 'blocked';
    const sb = statusById.get(b.id) || 'blocked';
    if (statusOrder[sa] !== statusOrder[sb]) return statusOrder[sa] - statusOrder[sb];
    if (a.starred && !b.starred) return -1;
    if (!a.starred && b.starred) return 1;
    if (sa === 'blocked' && sb === 'blocked') {
      const aBlocksB = b.blockedBy && b.blockedBy.some(ref => {
        const blocker = resolveBlocker(ref, byId, byTitle);
        return blocker && blocker.id === a.id;
      });
      if (aBlocksB) return -1;
      const bBlocksA = a.blockedBy && a.blockedBy.some(ref => {
        const blocker = resolveBlocker(ref, byId, byTitle);
        return blocker && blocker.id === b.id;
      });
      if (bBlocksA) return 1;
    }
    return 0;
  });
}

function getTaskType(task) {
  const notes = (task.notes || '').toLowerCase();
  if (notes.includes('agent')) return 'agent';
  if (notes.includes('design')) return 'design';
  if (notes.includes('manual')) return 'manual';
  return null;
}

function ProgressBar({ value, total, color }) {
  const pct = total > 0 ? Math.round((value / total) * 100) : 0;
  return (
    <div style={{
      width: '100%',
      height: '8px',
      background: ABLE_COLORS.bgSubtle,
      borderRadius: '999px',
      overflow: 'hidden'
    }}>
      <div style={{
        width: `${pct}%`,
        height: '100%',
        background: color,
        transition: 'width 0.3s ease'
      }} />
    </div>
  );
}

function StatCard({ label, value, color }) {
  return (
    <div style={{
      flex: 1,
      background: ABLE_COLORS.bg,
      border: `1px solid ${ABLE_COLORS.borderLight}`,
      borderRadius: ABLE_COLORS.radiusLarge,
      padding: '16px',
      boxShadow: ABLE_COLORS.cardShadow,
    }}>
      <div style={{ fontSize: '12px', color: ABLE_COLORS.textMuted, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.5px' }}>
        {label}
      </div>
      <div style={{ fontSize: '28px', color: color || ABLE_COLORS.textPrimary, fontWeight: 700, marginTop: '4px' }}>
        {value}
      </div>
    </div>
  );
}

function InlineEdit({ value, onSave, style, placeholder }) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(value);

  useEffect(() => { setDraft(value); }, [value]);

  if (!editing) {
    return (
      <span
        onClick={() => setEditing(true)}
        title="Click to edit"
        style={{ ...style, cursor: 'pointer' }}
      >
        {value || placeholder}
      </span>
    );
  }
  return (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: '4px' }}>
      <input
        autoFocus
        value={draft}
        onChange={e => setDraft(e.target.value)}
        onKeyDown={e => {
          if (e.key === 'Enter') { onSave(draft); setEditing(false); }
          if (e.key === 'Escape') { setDraft(value); setEditing(false); }
        }}
        style={{
          ...style,
          border: `1px solid ${ABLE_COLORS.border}`,
          borderRadius: '6px',
          padding: '2px 6px',
          background: 'white',
        }}
      />
      <button
        onClick={() => { onSave(draft); setEditing(false); }}
        title="Save"
        style={{ background: 'transparent', border: 'none', cursor: 'pointer', display: 'flex' }}
      >
        <Check size={14} color={STATUS_COLORS.done} />
      </button>
      <button
        onClick={() => { setDraft(value); setEditing(false); }}
        title="Cancel"
        style={{ background: 'transparent', border: 'none', cursor: 'pointer', display: 'flex' }}
      >
        <X size={14} color={ABLE_COLORS.textMuted} />
      </button>
    </span>
  );
}

export default function RoadmapView({
  tasks = [],
  onTaskAction,
  onNavigate,
  getToken,
  isAuthenticated,
}) {
  const roadmapConfig = useRoadmapConfig(getToken, isAuthenticated);
  const { showToast } = useToast();
  const lastShownConfigErrorRef = useRef(null);

  useEffect(() => {
    const err = roadmapConfig.syncError;
    if (err && err !== lastShownConfigErrorRef.current) {
      lastShownConfigErrorRef.current = err;
      showToast(`Couldn't save roadmap: ${err}`, { type: 'error' });
    }
    if (!err) lastShownConfigErrorRef.current = null;
  }, [roadmapConfig.syncError, showToast]);

  // Discover projects that have at least one phased task
  const projectsWithPhases = useMemo(() => {
    const seen = new Set();
    tasks.forEach(t => {
      if (t.project && t.phase != null) seen.add(t.project);
    });
    // Always include projects we have configs for
    Object.keys(roadmapConfig.configs).forEach(p => seen.add(p));
    return Array.from(seen).sort();
  }, [tasks, roadmapConfig.configs]);

  const [selectedProject, setSelectedProject] = useState(() => {
    return projectsWithPhases.includes('Storybond') ? 'Storybond' : (projectsWithPhases[0] || 'Storybond');
  });
  const [editMode, setEditMode] = useState(false);

  // Ensure config exists when switching projects
  useEffect(() => {
    if (selectedProject) roadmapConfig.ensureConfig(selectedProject);
  }, [selectedProject, roadmapConfig]);

  const config = roadmapConfig.getConfig(selectedProject) || { title: 'Roadmap', subtitle: '', phases: [] };
  const PHASES = config.phases;

  const [expandedPhases, setExpandedPhases] = useState({});

  // Auto-expand first phase when project changes
  useEffect(() => {
    if (PHASES[0]) {
      setExpandedPhases(prev => ({ ...prev, [PHASES[0].id]: prev[PHASES[0].id] ?? true }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedProject]);

  const roadmapTasks = useMemo(
    () => tasks.filter(t => t.project === selectedProject && t.phase != null),
    [tasks, selectedProject]
  );

  const taskLookups = useMemo(() => {
    const byId = new Map();
    const byTitle = new Map();
    tasks.forEach(t => {
      if (t.id != null) byId.set(t.id, t);
      if (t.title) byTitle.set(t.title.toLowerCase(), t);
    });
    return { byId, byTitle };
  }, [tasks]);

  const statusById = useMemo(() => {
    const map = new Map();
    tasks.forEach(t => {
      map.set(t.id, computeStatus(t, taskLookups.byId, taskLookups.byTitle));
    });
    return map;
  }, [tasks, taskLookups]);

  const tasksByPhase = useMemo(() => {
    const map = {};
    PHASES.forEach(p => { map[p.id] = []; });
    roadmapTasks.forEach(t => {
      if (map[t.phase]) map[t.phase].push(t);
    });
    return map;
  }, [roadmapTasks, PHASES]);

  const totals = useMemo(() => {
    let done = 0, ready = 0, blocked = 0;
    roadmapTasks.forEach(t => {
      const s = statusById.get(t.id);
      if (s === 'done') done++;
      else if (s === 'ready') ready++;
      else blocked++;
    });
    return { total: roadmapTasks.length, done, ready, blocked };
  }, [roadmapTasks, statusById]);

  const togglePhase = (id) => {
    setExpandedPhases(prev => ({ ...prev, [id]: !prev[id] }));
  };

  const handleTaskToggle = (task) => {
    if (onTaskAction) onTaskAction(task.id, { done: !task.done });
  };

  const handleTaskPhaseChange = (task, newPhaseId) => {
    if (onTaskAction) onTaskAction(task.id, { phase: newPhaseId });
  };

  const scrollToTask = (taskId) => {
    const task = taskLookups.byId.get(taskId);
    if (task && task.phase != null) {
      setExpandedPhases(prev => ({ ...prev, [task.phase]: true }));
    }
    setTimeout(() => {
      const el = document.getElementById(`roadmap-task-${taskId}`);
      if (el) {
        el.scrollIntoView({ behavior: 'smooth', block: 'center' });
        el.style.outline = `2px solid ${ABLE_COLORS.primary}`;
        setTimeout(() => { el.style.outline = 'none'; }, 1500);
      }
    }, 100);
  };

  const handleAddProject = () => {
    const name = window.prompt('Project name (must match the "project" tag on tasks):');
    if (!name || !name.trim()) return;
    const trimmed = name.trim();
    roadmapConfig.ensureConfig(trimmed);
    setSelectedProject(trimmed);
  };

  return (
    <div style={{ width: '100%', maxWidth: '900px', margin: '0 auto' }}>
      {/* Header */}
      <div style={{
        background: 'white',
        borderRadius: '20px',
        padding: '20px 24px',
        marginBottom: '20px',
        display: 'flex',
        alignItems: 'center',
        gap: '12px',
        boxShadow: '0 2px 12px rgba(0,0,0,0.06)'
      }}>
        <button
          onClick={() => onNavigate && onNavigate('hub')}
          style={{
            background: 'transparent',
            border: 'none',
            cursor: 'pointer',
            padding: '8px',
            display: 'flex',
            alignItems: 'center'
          }}
          title="Back to Hub"
        >
          <ArrowLeft size={24} color={ABLE_COLORS.primary} />
        </button>
        <div style={{ flex: 1, minWidth: 0 }}>
          <h1 style={{ margin: 0, fontSize: '24px', color: ABLE_COLORS.primary, fontWeight: 700 }}>
            <InlineEdit
              value={config.title}
              onSave={v => roadmapConfig.setHeader(selectedProject, { title: v })}
              placeholder="Title"
              style={{ fontSize: '24px', color: ABLE_COLORS.primary, fontWeight: 700 }}
            />
          </h1>
          <p style={{ margin: '4px 0 0 0', fontSize: '14px', color: ABLE_COLORS.textSecondary }}>
            <InlineEdit
              value={config.subtitle}
              onSave={v => roadmapConfig.setHeader(selectedProject, { subtitle: v })}
              placeholder="Subtitle"
              style={{ fontSize: '14px', color: ABLE_COLORS.textSecondary }}
            />
          </p>
        </div>
        <button
          onClick={() => setEditMode(e => !e)}
          title={editMode ? 'Done editing' : 'Edit phases'}
          style={{
            background: editMode ? ABLE_COLORS.primaryLight : 'transparent',
            border: `1px solid ${editMode ? ABLE_COLORS.primary : ABLE_COLORS.border}`,
            borderRadius: '8px',
            padding: '6px 10px',
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            gap: '6px',
            color: editMode ? ABLE_COLORS.primary : ABLE_COLORS.textSecondary,
            fontSize: '13px',
          }}
        >
          <Pencil size={14} />
          {editMode ? 'Done' : 'Edit'}
        </button>
      </div>

      {/* Project picker */}
      <div style={{
        background: 'white',
        borderRadius: ABLE_COLORS.radiusLarge,
        border: `1px solid ${ABLE_COLORS.borderLight}`,
        padding: '12px 16px',
        marginBottom: '20px',
        display: 'flex',
        alignItems: 'center',
        gap: '10px',
        flexWrap: 'wrap',
        boxShadow: ABLE_COLORS.cardShadow,
      }}>
        <span style={{
          fontSize: '12px',
          fontWeight: 600,
          color: ABLE_COLORS.textMuted,
          textTransform: 'uppercase',
          letterSpacing: '0.5px',
        }}>
          Project
        </span>
        {projectsWithPhases.map(project => (
          <button
            key={project}
            onClick={() => setSelectedProject(project)}
            style={{
              padding: '6px 12px',
              borderRadius: '999px',
              border: `1px solid ${project === selectedProject ? ABLE_COLORS.primary : ABLE_COLORS.border}`,
              background: project === selectedProject ? ABLE_COLORS.primaryLight : 'transparent',
              color: project === selectedProject ? ABLE_COLORS.primary : ABLE_COLORS.textPrimary,
              fontSize: '13px',
              fontWeight: project === selectedProject ? 600 : 500,
              cursor: 'pointer',
            }}
          >
            {project}
          </button>
        ))}
        <button
          onClick={handleAddProject}
          title="Add a project to track"
          style={{
            padding: '6px 10px',
            borderRadius: '999px',
            border: `1px dashed ${ABLE_COLORS.border}`,
            background: 'transparent',
            color: ABLE_COLORS.textMuted,
            fontSize: '13px',
            cursor: 'pointer',
            display: 'inline-flex',
            alignItems: 'center',
            gap: '4px',
          }}
        >
          <Plus size={14} /> Add project
        </button>
      </div>

      {/* Stats */}
      <div style={{ display: 'flex', gap: '12px', marginBottom: '20px' }}>
        <StatCard label="Total" value={totals.total} />
        <StatCard label="Done" value={totals.done} color={STATUS_COLORS.done} />
        <StatCard label="Ready" value={totals.ready} color={STATUS_COLORS.ready} />
        <StatCard label="Blocked" value={totals.blocked} color={STATUS_COLORS.blocked} />
      </div>

      {/* Overall progress */}
      <div style={{
        background: 'white',
        border: `1px solid ${ABLE_COLORS.borderLight}`,
        borderRadius: ABLE_COLORS.radiusLarge,
        padding: '20px',
        marginBottom: '20px',
        boxShadow: ABLE_COLORS.cardShadow,
      }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '10px' }}>
          <span style={{ fontSize: '14px', fontWeight: 600, color: ABLE_COLORS.textPrimary }}>
            Overall progress
          </span>
          <span style={{ fontSize: '14px', color: ABLE_COLORS.textMuted }}>
            {totals.done} / {totals.total}
          </span>
        </div>
        <ProgressBar value={totals.done} total={totals.total} color={ABLE_COLORS.primary} />
      </div>

      {/* Phases */}
      {PHASES.map((phase, phaseIdx) => {
        const phaseTasks = tasksByPhase[phase.id] || [];
        let doneCount = 0;
        let readyCount = 0;
        phaseTasks.forEach(t => {
          const s = statusById.get(t.id);
          if (s === 'done') doneCount++;
          else if (s === 'ready') readyCount++;
        });
        const isExpanded = !!expandedPhases[phase.id];

        return (
          <div
            key={phase.id}
            style={{
              background: 'white',
              border: `1px solid ${ABLE_COLORS.borderLight}`,
              borderRadius: ABLE_COLORS.radiusLarge,
              marginBottom: '12px',
              boxShadow: ABLE_COLORS.cardShadow,
              overflow: 'hidden'
            }}
          >
            <div
              style={{
                width: '100%',
                padding: '16px 20px',
                display: 'flex',
                alignItems: 'center',
                gap: '12px',
              }}
            >
              <div style={{
                width: '14px',
                height: '14px',
                borderRadius: '50%',
                background: phase.color,
                flexShrink: 0
              }} />
              <div
                onClick={() => !editMode && togglePhase(phase.id)}
                style={{ flex: 1, minWidth: 0, cursor: editMode ? 'default' : 'pointer' }}
              >
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '6px', gap: '8px' }}>
                  <span style={{ fontSize: '15px', fontWeight: 600, color: ABLE_COLORS.textPrimary, display: 'inline-flex', alignItems: 'center', gap: '6px' }}>
                    Phase {phase.id}:{' '}
                    {editMode ? (
                      <InlineEdit
                        value={phase.name}
                        onSave={v => roadmapConfig.renamePhase(selectedProject, phase.id, v)}
                        placeholder="Phase name"
                        style={{ fontSize: '15px', fontWeight: 600, color: ABLE_COLORS.textPrimary }}
                      />
                    ) : phase.name}
                  </span>
                  <span style={{ fontSize: '12px', color: ABLE_COLORS.textMuted, marginLeft: '12px', flexShrink: 0 }}>
                    {doneCount}/{phaseTasks.length}{readyCount > 0 ? `, ${readyCount} ready` : ''}
                  </span>
                </div>
                <ProgressBar value={doneCount} total={phaseTasks.length} color={phase.color} />
              </div>
              {editMode ? (
                <div style={{ display: 'flex', gap: '4px', alignItems: 'center' }}>
                  <button
                    onClick={() => roadmapConfig.movePhase(selectedProject, phase.id, 'up')}
                    disabled={phaseIdx === 0}
                    title="Move up"
                    style={{
                      background: 'transparent', border: 'none',
                      cursor: phaseIdx === 0 ? 'default' : 'pointer',
                      opacity: phaseIdx === 0 ? 0.3 : 1,
                      padding: '4px', display: 'flex',
                    }}
                  >
                    <ChevronUp size={16} color={ABLE_COLORS.textMuted} />
                  </button>
                  <button
                    onClick={() => roadmapConfig.movePhase(selectedProject, phase.id, 'down')}
                    disabled={phaseIdx === PHASES.length - 1}
                    title="Move down"
                    style={{
                      background: 'transparent', border: 'none',
                      cursor: phaseIdx === PHASES.length - 1 ? 'default' : 'pointer',
                      opacity: phaseIdx === PHASES.length - 1 ? 0.3 : 1,
                      padding: '4px', display: 'flex',
                    }}
                  >
                    <ChevronDown size={16} color={ABLE_COLORS.textMuted} />
                  </button>
                  <button
                    onClick={() => {
                      if (phaseTasks.length > 0) {
                        if (!window.confirm(`Phase "${phase.name}" has ${phaseTasks.length} task(s). Remove the phase definition anyway? Tasks will keep their phase number but won't display until you re-add a phase with id ${phase.id}.`)) return;
                      }
                      roadmapConfig.removePhase(selectedProject, phase.id);
                    }}
                    title="Remove phase"
                    style={{
                      background: 'transparent', border: 'none', cursor: 'pointer',
                      padding: '4px', display: 'flex',
                    }}
                  >
                    <Trash2 size={16} color={STATUS_COLORS.blocked} />
                  </button>
                </div>
              ) : (
                <button
                  onClick={() => togglePhase(phase.id)}
                  style={{ background: 'transparent', border: 'none', cursor: 'pointer', padding: '4px', display: 'flex' }}
                  title={isExpanded ? 'Collapse' : 'Expand'}
                >
                  {isExpanded
                    ? <ChevronDown size={18} color={ABLE_COLORS.textMuted} />
                    : <ChevronRight size={18} color={ABLE_COLORS.textMuted} />}
                </button>
              )}
            </div>

            {isExpanded && (
              <div style={{ padding: '0 20px 16px 20px' }}>
                {phaseTasks.length === 0 ? (
                  <div style={{ fontSize: '13px', color: ABLE_COLORS.textMuted, padding: '8px 0' }}>
                    No tasks in this phase yet.
                  </div>
                ) : (
                  sortPhaseTasks(phaseTasks, statusById, taskLookups.byId, taskLookups.byTitle).map(task => {
                    const status = statusById.get(task.id);
                    const type = getTaskType(task);
                    const blockerLabels = status === 'blocked' && task.blockedBy
                      ? task.blockedBy.map(ref => {
                          const blocker = resolveBlocker(ref, taskLookups.byId, taskLookups.byTitle);
                          return blocker?.title || String(ref);
                        })
                      : null;
                    return (
                      <div
                        key={task.id}
                        id={`roadmap-task-${task.id}`}
                        style={{
                          display: 'flex',
                          alignItems: 'flex-start',
                          gap: '10px',
                          padding: '10px 12px',
                          marginTop: '6px',
                          background: STATUS_BG[status] || ABLE_COLORS.bg,
                          border: `1px solid ${status === 'blocked' ? 'rgba(184, 134, 11, 0.15)' : ABLE_COLORS.borderLight}`,
                          borderLeft: `3px solid ${STATUS_COLORS[status]}`,
                          borderRadius: ABLE_COLORS.radiusMedium,
                        }}
                      >
                        <input
                          type="checkbox"
                          checked={!!task.done}
                          onChange={() => handleTaskToggle(task)}
                          aria-label={`Mark "${task.title}" as ${task.done ? 'not done' : 'done'}`}
                          title={task.done ? 'Mark as not done' : 'Mark as done'}
                          style={{
                            width: '16px',
                            height: '16px',
                            marginTop: '2px',
                            accentColor: STATUS_COLORS[status],
                            cursor: 'pointer',
                            flexShrink: 0
                          }}
                        />
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
                            <span style={{
                              fontSize: '14px',
                              color: ABLE_COLORS.textPrimary,
                              fontWeight: 500,
                              textDecoration: 'none',
                              opacity: task.done ? 0.5 : 1
                            }}>
                              {task.title}
                            </span>
                            {type && (
                              <span style={{
                                fontSize: '10px',
                                fontWeight: 600,
                                textTransform: 'uppercase',
                                letterSpacing: '0.5px',
                                padding: '2px 6px',
                                borderRadius: ABLE_COLORS.radiusSmall,
                                background: ABLE_COLORS.bgSubtle,
                                color: ABLE_COLORS.textSecondary
                              }}>
                                {type}
                              </span>
                            )}
                            {task.starred && (
                              <Star size={12} color={ABLE_COLORS.accent} fill={ABLE_COLORS.accent} />
                            )}
                            {editMode && (
                              <select
                                value={task.phase ?? ''}
                                onChange={(e) => handleTaskPhaseChange(task, parseInt(e.target.value, 10))}
                                title="Move task to a different phase"
                                style={{
                                  fontSize: '11px',
                                  padding: '2px 4px',
                                  borderRadius: '4px',
                                  border: `1px solid ${ABLE_COLORS.border}`,
                                  background: 'white',
                                  color: ABLE_COLORS.textSecondary,
                                  cursor: 'pointer',
                                }}
                              >
                                {PHASES.map(p => (
                                  <option key={p.id} value={p.id}>Phase {p.id}: {p.name}</option>
                                ))}
                              </select>
                            )}
                          </div>
                          {blockerLabels && blockerLabels.length > 0 && (
                            <div style={{ fontSize: '12px', color: ABLE_COLORS.textMuted, marginTop: '3px' }}>
                              Blocked by:{' '}
                              {blockerLabels.map((label, i) => {
                                const blocker = resolveBlocker(
                                  task.blockedBy[i], taskLookups.byId, taskLookups.byTitle
                                );
                                return (
                                  <span key={i}>
                                    {i > 0 && ', '}
                                    {blocker ? (
                                      <span
                                        onClick={(e) => { e.stopPropagation(); scrollToTask(blocker.id); }}
                                        style={{
                                          cursor: 'pointer',
                                        }}
                                        onMouseEnter={(e) => { e.target.style.color = ABLE_COLORS.primary; }}
                                        onMouseLeave={(e) => { e.target.style.color = 'inherit'; }}
                                      >
                                        {label}
                                      </span>
                                    ) : label}
                                  </span>
                                );
                              })}
                            </div>
                          )}
                        </div>
                      </div>
                    );
                  })
                )}
              </div>
            )}
          </div>
        );
      })}

      {editMode && (
        <button
          onClick={() => roadmapConfig.addPhase(selectedProject)}
          style={{
            width: '100%',
            padding: '12px',
            background: 'transparent',
            border: `1px dashed ${ABLE_COLORS.border}`,
            borderRadius: ABLE_COLORS.radiusLarge,
            cursor: 'pointer',
            color: ABLE_COLORS.textMuted,
            fontSize: '14px',
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: '6px',
          }}
        >
          <Plus size={16} /> Add phase
        </button>
      )}

      {!editMode && PHASES.length === 0 && (
        <div style={{
          background: 'white',
          border: `1px dashed ${ABLE_COLORS.border}`,
          borderRadius: ABLE_COLORS.radiusLarge,
          padding: '32px',
          textAlign: 'center',
          color: ABLE_COLORS.textMuted,
          fontSize: '14px',
        }}>
          No phases yet. Click <strong>Edit</strong> to add some.
        </div>
      )}
    </div>
  );
}
